import { useState, type ReactNode } from 'react'
import {
  formatMoney,
  usePlanCatalogue,
  type BillingPeriod,
  type PlanCatalogueEntry,
  type SignupPath,
} from '@lb/domain'
import { useTranslation } from '@lb/i18n'
import { Button, EmptyState, IconArrowForward, IconCheck, Skeleton, cn } from '@lb/ui'

import { publicHttp } from '../../lib/client'
import { useContentLocale } from '../../lib/content-locale'
import { FunnelShell } from './funnel-shell'
import { chipUsesPlanName, defaultOrgPlan } from './org-plan-choice'

/**
 * The landing page — plan cards, per audience (spec §3.1).
 *
 * ✅ Unblocked 2026-08-01, but NOT the way we asked.
 *
 * We asked for `@Public()` on `GET /api/plans`. The backend refused and was
 * right: `Plan` carries `versions[]` — every price ever charged, including what
 * grandfathered subscribers still pay — plus `stripePriceId` and archived plans
 * by default. They built `GET /api/plans/catalogue` instead, a separate public
 * contract carrying only the sellable revision, flattened.
 *
 * So this reads `PlanCatalogueEntry`, where the price sits on the entry itself
 * rather than under `sellableVersion`, and an archived plan cannot appear at
 * all because the catalogue only lists plans that have one.
 *
 * ⚠️ Known noise on the shared development API: the catalogue is currently
 * answering with ~240 rows, of which five are real — the rest are leftovers
 * from the backend's own e2e suite, left ACTIVE and therefore sellable. Their
 * suite now retires what it creates; clearing the existing rows is escalated to
 * the operator. **We do not filter them client-side by key prefix**: the
 * backend settled that a test-row naming convention stops being a convention
 * the moment production code depends on it, and then a legitimately-named plan
 * is invisible for a reason nobody can find.
 */

/** Labels come from `onboarding.audience.*` — the table holds only the key. */
const AUDIENCES: { path: SignupPath }[] = [
  { path: 'PARENT' },
  { path: 'PRO' },
  { path: 'TSP' },
  { path: 'ORG' },
]

/** Quarterly is pre-selected, per the design. */
const PREFERRED_PERIOD: BillingPeriod = 'QUARTER'

export function LandingScreen({
  onChoose,
  initialAudience = 'PARENT',
}: {
  onChoose: (path: SignupPath, planKey: string, seats?: number) => void
  /** The tab to open on — the website links each audience to its own. */
  initialAudience?: SignupPath
}) {
  const { t } = useTranslation()
  const locale = useContentLocale()
  const [audience, setAudience] = useState<SignupPath>(initialAudience)
  const plans = usePlanCatalogue(publicHttp, audience, locale)

  /*
   * No client-side audience filter: the catalogue is filtered server-side by
   * `?audience=`, and the four tabs map one-to-one onto `SignupPath`.
   *
   * `PlanGroup` used to be `PARENT | OTHER`, so Praticien, TSP and Organisation
   * all showed the same two plans and a practitioner was offered a seat licence
   * for an organisation they do not run. That is gone.
   */
  const visible = [...(plans.data?.items ?? [])].sort((a, b) => a.position - b.position)

  /*
   * Exactly ONE card is featured, and the server decides when it can.
   *
   * This was `plan.highlighted || plan.period === PREFERRED_PERIOD`, which is
   * two independent truths OR'd together — so the moment the catalogue marks a
   * plan other than the quarterly one, BOTH light up: two ink borders, two
   * ribbons, and no answer to "which one are you recommending". Staging does
   * exactly that today (`annual` is `highlighted`, quarterly is not).
   *
   * The quarterly preference is a FALLBACK for a catalogue that highlights
   * nothing, not a second opinion alongside one that does.
   */
  const highlighted = visible.find((plan) => plan.highlighted)
  const featuredId = (highlighted ?? visible.find((plan) => plan.period === PREFERRED_PERIOD))?.id

  return (
    <FunnelShell
      wide
      eyebrow={t(`onboarding.landingEyebrow.${audience}`)}
      title={t(`onboarding.landingTitleBy.${audience}`)}
      lede={t(`onboarding.landingLedeBy.${audience}`)}
    >
      {/*
       * `.ob-audience` — a segmented control, not a chip row.
       *
       * The difference is not decoration: a track with one raised pill reads as
       * "four states of one thing", which is what an audience switch is. Four
       * loose chips with one filled dark read as four separate buttons, one of
       * which is on.
       *
       * Still a radiogroup. The prototype uses `role="tablist"`, but nothing
       * here is a tab panel — the plan grid below is the same region with a
       * different dataset, and a radiogroup is what that is.
       */}
      <div
        role="radiogroup"
        aria-label={t('onboarding.accountType')}
        className="mx-auto -mt-2 mb-11 flex w-fit max-w-full flex-wrap justify-center gap-1.5 rounded-full bg-cream-dark p-[5px]"
      >
        {AUDIENCES.map((option) => (
          <button
            key={option.path}
            role="radio"
            aria-checked={audience === option.path}
            onClick={() => setAudience(option.path)}
            className={cn(
              'min-h-[44px] rounded-full px-[22px] text-[13.5px] font-semibold transition-colors',
              audience === option.path
                ? 'bg-surface text-ink shadow-[0_2px_8px_rgb(41_51_57/0.08)]'
                : 'text-slate hover:text-ink',
            )}
          >
            {t(`onboarding.audience.${option.path}`)}
          </button>
        ))}
      </div>

      {plans.isPending ? (
        <PlanGridSkeleton />
      ) : plans.isError ? (
        <EmptyState
          title={t('onboarding.plansErrorTitle')}
          body={t('onboarding.plansErrorBody')}
        />
      ) : visible.length === 0 ? (
        <EmptyState
          title={t('onboarding.plansEmptyTitle')}
          body={t('onboarding.plansEmptyBody')}
        />
      ) : audience === 'ORG' ? (
        /*
         * The organisation tab is not a card grid — the design draws a seat
         * slider, a billing-cycle choice and a live-pricing panel. Driven by
         * the same catalogue: one chip per per-seat ORG plan, and the volume
         * discount line only when the selected plan carries a tier table.
         */
        <OrgSeatsAndPricing
          plans={visible}
          onContinue={(planKey, seats) => onChoose('ORG', planKey, seats)}
        />
      ) : (
        <>
          {/*
           * `align-items: stretch` and `mt-11` from `.ob-plans-grid`. The
           * ribbon overhangs the card by 12px, so the grid needs headroom or
           * the featured card's flag is clipped by the control above it.
           */}
          <div className="grid items-stretch gap-gap-card pt-3 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                featured={plan.id === featuredId}
                cheapestPerMonth={cheapestMonthly(visible)}
                onChoose={() => onChoose(audience, plan.key)}
              />
            ))}
          </div>
          {/*
           * `.ob-plans-foot`. Not decoration — it is where the buyer is told
           * that the price shown is not the price charged, which Quebec sales
           * tax makes true for every plan on this page.
           */}
          <p className="mt-[30px] text-center text-[13.5px] text-slate">{t('onboarding.plansFoot')}</p>
        </>
      )}
    </FunnelShell>
  )
}

/** Québec sales tax, for the live-pricing ESTIMATE only — the checkout quote is authoritative. */
const GST_PPM = 50_000
const QST_PPM = 99_750

const SEAT_MIN = 10
const SEAT_MAX = 50

/**
 * The organisation tab — « pick your seats and billing cycle, volume discount
 * applied live » (spec's org licences screen).
 *
 * Everything here is the catalogue's data arranged as the design draws it:
 * the cycle chips are the per-seat ORG plans (one today — Monthly; Quarterly
 * and Annual appear the day the operator prices them), and the volume
 * discount line renders only when the selected plan carries tiers, because a
 * percentage invented client-side is a price nobody authorised. The taxes are
 * an estimate at the stated rates; the checkout quote stays authoritative.
 */
function OrgSeatsAndPricing({
  plans,
  onContinue,
}: {
  plans: PlanCatalogueEntry[]
  onContinue: (planKey: string, seats: number) => void
}) {
  const { t, i18n } = useTranslation()
  const [seats, setSeats] = useState(25)
  const perSeat = plans.filter((plan) => plan.mode === 'PER_SEAT')
  const [selectedKey, setSelectedKey] = useState(defaultOrgPlan(perSeat)?.key)
  const plan = perSeat.find((candidate) => candidate.key === selectedKey) ?? perSeat[0]

  if (!plan) {
    return <EmptyState title={t('onboarding.plansEmptyTitle')} body={t('onboarding.plansEmptyBody')} />
  }

  const money = (cents: number) => formatMoney({ cents, currency: plan.currency }, i18n.language)

  const tier = plan.tiers.find(
    (candidate) => seats >= candidate.minSeats && (candidate.maxSeats === null || seats <= candidate.maxSeats),
  )
  const baseCents = plan.priceCents * seats
  const tieredUnit = tier ? tier.unitPriceCents : plan.priceCents
  const discountCents = (plan.priceCents - tieredUnit) * seats
  const netCents = baseCents - discountCents
  const gstCents = Math.round((netCents * GST_PPM) / 1_000_000)
  const qstCents = Math.round((netCents * QST_PPM) / 1_000_000)
  const totalCents = netCents + gstCents + qstCents

  const tierRange = tier ? `${tier.minSeats}–${tier.maxSeats ?? '∞'}` : null
  const tierPercent = tier ? Math.round((1 - tier.unitPriceCents / plan.priceCents) * 100) : 0

  return (
    <div className="mx-auto grid w-full max-w-[980px] items-start gap-gap-card pt-3 lg:grid-cols-[1fr_420px]">
      {/* ── LEFT: seats + cycle ─────────────────────────────────────────── */}
      <div className="rounded-[var(--r-lg)] border border-border bg-surface p-card-lg">
        <p className="text-[14px] font-semibold text-ink">
          {t('onboarding.orgSeatsLabel')}{' '}
          <span className="font-normal text-muted">{t('onboarding.orgSeatsRange')}</span>
        </p>
        <div className="mt-4 flex items-center gap-4">
          <input
            type="range"
            min={SEAT_MIN}
            max={SEAT_MAX}
            step={1}
            value={seats}
            onChange={(event) => setSeats(Number.parseInt(event.target.value, 10))}
            aria-label={t('onboarding.orgSeatsLabel')}
            className="w-full accent-[var(--ink)]"
          />
          <span className="grid h-14 w-16 flex-none place-items-center rounded-[var(--r-sm)] bg-cream-dark font-serif text-[24px] font-bold tabular-nums text-ink">
            {seats}
          </span>
        </div>
        <p className="mt-3 text-[13px] text-muted">{t('onboarding.orgSeatsMore')}</p>

        <p className="mt-7 text-[14px] font-semibold text-ink">{t('onboarding.billingCycle')}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {perSeat.map((candidate) => (
            <button
              key={candidate.key}
              type="button"
              aria-pressed={candidate.key === plan.key}
              onClick={() => setSelectedKey(candidate.key)}
              className={cn(
                'min-h-[44px] rounded-full border px-5 text-[14px] font-semibold transition-colors',
                candidate.key === plan.key
                  ? 'border-transparent bg-selected text-ink-on-tint'
                  : 'border-border text-ink-soft hover:border-ink-soft',
              )}
            >
              {chipUsesPlanName(perSeat, candidate)
                ? candidate.name
                : t(`onboarding.cycle.${candidate.period}`)}
            </button>
          ))}
        </div>
        <p className="mt-3 text-[13px] text-muted">
          {t('onboarding.perSeatRate', {
            rate: money(plan.priceCents),
            cycle: t(`onboarding.cycleWord.${plan.period}`),
          })}
        </p>

        {tier ? (
          <>
            <hr className="my-5 border-dashed border-border" />
            <p className="text-[13.5px] text-ink-soft">
              {t('onboarding.tierNote', { range: tierRange, percent: tierPercent })}
            </p>
          </>
        ) : null}
      </div>

      {/* ── RIGHT: live pricing ─────────────────────────────────────────── */}
      <div className="rounded-[var(--r-lg)] border border-border bg-surface p-card-lg">
        <p className="font-serif text-[22px] font-bold text-ink">{t('onboarding.livePricing')}</p>

        <dl className="mt-5 flex flex-col gap-2.5 text-[14px]">
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-ink-soft">
              {t('onboarding.seatsTimesRate', {
                seats,
                rate: money(plan.priceCents),
                cycle: t(`onboarding.cycle.${plan.period}`),
              })}
            </dt>
            <dd className="flex-none whitespace-nowrap font-num tabular-nums text-ink">{money(baseCents)}</dd>
          </div>

          {discountCents > 0 ? (
            /*
             * The amount never wraps and the tier detail sits under the
             * label: on a phone « −140,00 $ » broke across two lines beside
             * a long label (UAT). `whitespace-nowrap` + `flex-none` on every
             * amount, and the detail as its own line.
             */
            <div className="flex items-baseline justify-between gap-3">
              <dt className="min-w-0">
                <span className="font-semibold text-ink-soft">{t('onboarding.volumeDiscount')}</span>
                <span className="block text-[12px] text-muted">
                  {t('onboarding.volumeDiscountDetail', { range: tierRange, percent: tierPercent })}
                </span>
              </dt>
              <dd className="flex-none whitespace-nowrap font-num tabular-nums text-ink">
                − {money(discountCents)}
              </dd>
            </div>
          ) : null}

          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-ink-soft">{t('onboarding.gstLine')}</dt>
            <dd className="flex-none whitespace-nowrap font-num tabular-nums text-ink">{money(gstCents)}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-ink-soft">{t('onboarding.qstLine')}</dt>
            <dd className="flex-none whitespace-nowrap font-num tabular-nums text-ink">{money(qstCents)}</dd>
          </div>
        </dl>

        <hr className="my-4 border-border-soft" />

        <div className="flex items-baseline justify-between gap-3">
          <p className="text-[16px] font-bold text-ink">{t(`onboarding.totalPerCycle.${plan.period}`)}</p>
          <p className="font-num text-[26px] font-semibold tabular-nums text-ink">{money(totalCents)}</p>
        </div>
        <p className="mt-1 text-[12px] text-muted">{t('onboarding.estimateNote')}</p>

        <p className="mt-4 rounded-md bg-cream-dark p-3 text-[12.5px] text-ink-soft">
          {t(tier ? 'onboarding.orgRenewsTiered' : 'onboarding.orgRenews', {
            cycle: t(`onboarding.cycleWord.${plan.period}`),
            total: money(totalCents),
          })}
        </p>

        <Button size="lg" fullWidth className="mt-5" onClick={() => onContinue(plan.key, seats)}>
          {t('onboarding.orgContinue')}
          <IconArrowForward size={16} title={null} />
        </Button>
        <p className="mt-3 text-center text-[12.5px] text-muted">{t('onboarding.orgPayBelow')}</p>
      </div>
    </div>
  )
}

/**
 * The savings badge, computed rather than authored.
 *
 * Normalising every plan to a monthly figure is the only honest way to compare
 * a quarterly against an annual, and it is what the design's "Save 44%" claims.
 * The catalogue carries only the sellable revision, so there is no price
 * history here to accidentally compare against — the contract removed that
 * whole class of mistake rather than leaving it to us to avoid.
 */
function monthlyCents(plan: PlanCatalogueEntry): number {
  const months = plan.period === 'YEAR' ? 12 : plan.period === 'QUARTER' ? 3 : 1
  return Math.round(plan.priceCents / months)
}

function cheapestMonthly(plans: PlanCatalogueEntry[]): number {
  const values = plans.map(monthlyCents)
  return values.length ? Math.max(...values) : 0
}

/**
 * `.ob-plan-card`.
 *
 * ── What is data and what is design ───────────────────────────────────────
 * The prototype hard-codes three cards, their flags, their notes and their
 * three inclusion rows each. All of that is `PlanCatalogueEntry` here, so the
 * card renders the SHAPE the prototype specifies and lets the catalogue fill
 * it — and where the catalogue is silent, the block is absent rather than
 * padded with the prototype's English strings.
 *
 * That distinction is worth stating because it is currently visible: staging
 * answers with `features: []`, `badge: null` and `highlighted: false`, so a
 * card there is legitimately barer than the prototype. Against the mocks
 * (`pnpm dev:mocks`) the same component fills out.
 */
function PlanCard({
  plan,
  featured,
  cheapestPerMonth,
  onChoose,
}: {
  plan: PlanCatalogueEntry
  featured: boolean
  cheapestPerMonth: number
  onChoose: () => void
}) {
  const { t, i18n } = useTranslation()
  const perMonth = monthlyCents(plan)
  const saves =
    cheapestPerMonth > 0 && perMonth < cheapestPerMonth
      ? Math.round(((cheapestPerMonth - perMonth) / cheapestPerMonth) * 100)
      : 0

  /* `.ob-plan-note` — the note line under the price. Authored if the
   * catalogue says something, computed from the prices if it does not. */
  const note = plan.subLabel ?? (saves > 0 ? t('onboarding.saves', { percent: saves }) : null)

  return (
    <div
      className={cn(
        'relative flex flex-col rounded-[var(--r-lg)] border-[1.5px] bg-surface px-7 py-[30px]',
        'transition-[border-color,transform,box-shadow] duration-200 hover:-translate-y-0.5',
        'hover:border-ink-soft hover:shadow-[0_10px_28px_rgb(41_51_57/0.08)]',
        featured
          ? 'border-ink shadow-[0_12px_32px_rgb(41_51_57/0.10)]'
          : 'border-border',
      )}
    >
      {/*
       * `.ob-plan-flag` — a ribbon straddling the top edge, not a chip in the
       * corner. Ink on the featured card, bleu gris on the others.
       */}
      {plan.badge ? (
        <span
          className={cn(
            'absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-3.5 py-[5px]',
            'text-[11px] font-bold uppercase tracking-[0.1em]',
            featured ? 'bg-ink text-cream' : 'bg-bleu-gris text-ink-on-tint',
          )}
        >
          {badgeLabel(plan.badge, t)}
        </span>
      ) : null}

      <h2 className="font-serif text-[21px] font-bold leading-tight text-ink">{plan.name}</h2>

      {/* `.ob-plan-price` — cadence on the baseline beside the amount, not
       * stacked under it. The stacked version read as two facts; this reads
       * as one price. */}
      <p className="mt-3.5 flex flex-wrap items-baseline gap-1.5">
        <span className="font-serif text-[42px] font-bold tracking-[-0.01em] tabular-nums text-ink">
          {formatMoney({ cents: plan.priceCents, currency: plan.currency }, i18n.language)}
        </span>
        <span className="text-[14px] font-medium text-slate">
          {t(`onboarding.period.${plan.period}`)}
          {plan.mode === 'PER_SEAT' ? t('onboarding.perSeat') : ''}
        </span>
      </p>

      {/* `min-height` even when empty, so three cards keep one baseline. */}
      <p className="mt-1 min-h-[20px] text-[13.5px] font-semibold text-ink">{note}</p>

      {plan.features.length > 0 ? (
        <ul className="mt-3.5 flex flex-col gap-[9px]">
          {plan.features.map((feature) => (
            <li key={feature} className="flex items-start gap-[9px] text-[13.5px] text-ink-soft">
              <IconCheck size={13} title={null} className="mt-[3px] shrink-0 text-ink" />
              {feature}
            </li>
          ))}
        </ul>
      ) : null}

      {/* `margin-top: auto` — the CTA sits on the card's floor, so buttons line
       * up across a row whose cards hold different numbers of inclusions. */}
      <div className="mt-auto pt-6">
        <PlanButton featured={featured} onClick={onChoose}>
          {t('onboarding.chooseNamed', { plan: plan.name })}
        </PlanButton>
      </div>
    </div>
  )
}

/**
 * Filled on the featured card, ghost on the rest — the prototype's
 * `btn-primary wide` / `btn-ghost wide`, both full width.
 *
 * Not `PrimaryButton`: that one is `sm:w-auto` and square-cornered, which is
 * right for a form's submit and wrong for a pricing card, where three CTAs
 * side by side have to be the same width and pill-shaped.
 */
function PlanButton({
  featured,
  children,
  onClick,
}: {
  featured: boolean
  children: ReactNode
  onClick: () => void
}) {
  return (
    <Button variant={featured ? 'primary' : 'secondary'} fullWidth onClick={onClick}>
      {children}
    </Button>
  )
}

function PlanGridSkeleton() {
  return (
    <div className="grid gap-gap-card sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="rounded-[var(--r-lg)] border border-border bg-surface p-card">
          <Skeleton className="mb-4 h-6 w-32" />
          <Skeleton className="mb-2 h-8 w-24" />
          <Skeleton className="mb-6 h-4 w-20" />
          <Skeleton className="h-4 w-full" />
        </div>
      ))}
    </div>
  )
}

/**
 * The catalogue's badge is either a key the back office picks from a list
 * (`most-popular`, `best-value`) or free text an editor typed (« 9 mois »).
 * The keys were printed as they are — « MOST-POPULAR » on a French page
 * (client screenshot, 2 Oct 2026) — so a known key is translated and anything
 * else is shown as written.
 */
export function badgeLabel(badge: string, t: (key: string) => string): string {
  return badge === 'most-popular' || badge === 'best-value' ? t(`onboarding.badge.${badge}`) : badge
}
